import { useSyncExternalStore } from "react"

import type { WizardPanel, WizardStep } from "./wizard-steps.ts"

/** The one Full Project Execution draft open in the wizard, shown or minimized. */
export interface FpeTask {
  analysisId: string
  minimized: boolean
  /** Where to land when the wizard opens; the draft's remembered step otherwise. */
  step?: WizardStep
  panel?: WizardPanel | null
  /** "Start from a project / lead": the destination to preselect on a new draft. */
  presetKind?: "project" | "lead"
}

const KEY = "ai-center.fpeTask"
const listeners = new Set<() => void>()

// Kept for the tab's life, like EZ-Sign's minimized wizard, so a reload offers the
// same draft back. The draft itself is on the server; this only says which one was open.
let current: FpeTask | null = (() => {
  try {
    const raw = sessionStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as FpeTask) : null
  } catch {
    return null
  }
})()

const publish = (next: FpeTask | null) => {
  current = next
  try {
    if (next)
      sessionStorage.setItem(
        KEY,
        JSON.stringify({
          analysisId: next.analysisId,
          minimized: next.minimized
        })
      )
    else sessionStorage.removeItem(KEY)
  } catch {
    // Memory still holds it.
  }
  listeners.forEach((listener) => listener())
}

// The open wizard's question before it is replaced: save, discard or stay. It
// answers true once the user has saved or discarded, false to keep editing.
let leaveGuard: (() => Promise<boolean>) | null = null
export const setFpeLeaveGuard = (guard: (() => Promise<boolean>) | null) => {
  leaveGuard = guard
}

// One wizard at a time: opening a draft that is already open restores it rather
// than starting a second copy, and opening another draft replaces it — once the
// open one has been saved or discarded, which the user is asked about first. With
// nothing to ask it opens at once, so a caller reads the new task straight away.
export const openFpeWizard = (
  analysisId: string,
  options: Omit<FpeTask, "analysisId" | "minimized"> = {}
): Promise<boolean> => {
  const open = () => {
    publish({ analysisId, minimized: false, ...options })
    return true
  }
  if (current && current.analysisId !== analysisId && leaveGuard)
    return leaveGuard().then((left) => left && open())
  return Promise.resolve(open())
}

export const minimizeFpeWizard = () =>
  current && publish({ ...current, minimized: true })

export const restoreFpeWizard = () =>
  current && publish({ ...current, minimized: false })

// The draft just closed, so AI Center can say where it went. Memory only: a notice,
// not state worth keeping across a reload.
let lastClosed: string | null = null

/** Closes the wizard. Only a draft that was saved is announced in AI Center. */
export const closeFpeTask = (
  outcome: "saved" | "discarded" | "unchanged" = "saved"
) => {
  lastClosed = outcome === "saved" ? (current?.analysisId ?? null) : null
  publish(null)
}

export const takeLastClosed = () => {
  const id = lastClosed
  lastClosed = null
  return id
}

/** Clears the one-shot landing hints once the wizard has used them. */
export const settleFpeTask = () =>
  current &&
  (current.step || current.panel || current.presetKind) &&
  publish({ analysisId: current.analysisId, minimized: current.minimized })

export const useFpeTask = () =>
  useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => current
  )
